import { ErrorHandler, Injectable } from '@angular/core';
import * as Sentry from '@sentry/browser';
import { User } from '@sentry/browser';

// @ts-ignore
import appMetadata from '../../../../../apps/hosty-web/src/assets/version.json';
import { environment } from '../../../../../apps/hosty-web/src/environments/environment';

const DSN = 'https://ca17129e9dc44970b5491f6af4c98261@o468620.ingest.sentry.io/6223649';

// 'Expired JWT Token' was previously ignored here. It has been removed
// deliberately: that message is exactly what the auth endpoints return when a
// session is being dropped, so the filter was suppressing the only signal that
// could explain the recurring "the app logged me out again" reports.
const IGNORE_ERRORS = ['jQuery is not defined', 'OneSignal: This web push config can'];

/**
 * Distinguishes web from mobile and production from stage, so events from the
 * Cordova app can be isolated in Sentry.
 */
const releaseName = (): string =>
  `hosty-${environment.isMobile ? 'mob' : 'web'}@${appMetadata.version}`;

const environmentName = (): string =>
  `${environment.isMobile ? 'mobile' : 'web'}-${environment.production ? 'production' : 'stage'}`;

/**
 * Must be called from main.ts *before* bootstrapApplication, so that failures
 * during bootstrap — which surface to the user as an app that never starts —
 * are reported too.
 */
export function initSentry(): void {
  if (!environment.sentryEnabled) {
    return;
  }
  Sentry.init({
    dsn: DSN,
    release: releaseName(),
    environment: environmentName(),
    ignoreErrors: IGNORE_ERRORS,
    // Never attach IPs, cookies or request headers. This app handles guest and
    // reservation data on behalf of hosts; none of it belongs in an error
    // tracker. Explicit rather than relying on the SDK default.
    sendDefaultPii: false,
    beforeSend: labelHttpErrors,
  });
}

/**
 * Makes HTTP failures legible however they reach Sentry.
 *
 * SentryErrorHandler only sees what Angular routes to ErrorHandler. An
 * HttpErrorResponse thrown inside a zone task escapes that path and is caught by
 * the SDK's own browser instrumentation, which serialises it as the useless
 * "Object captured as exception with keys: error, headers, message, ..." — no
 * status, no URL, and every occurrence grouped together regardless of endpoint.
 */
function labelHttpErrors(event: Sentry.ErrorEvent, hint: Sentry.EventHint): Sentry.ErrorEvent {
  const original = hint?.originalException as Record<string, unknown> | undefined;
  if (!original || typeof original !== 'object' || original['name'] !== 'HttpErrorResponse') {
    return event;
  }

  const status = original['status'];
  const path = sanitizeUrl(original['url'] as string);
  const value = `HTTP ${status} ${original['statusText'] ?? ''} ${path}`
    .replace(/\s+/g, ' ')
    .trim();

  if (event.exception?.values?.[0]) {
    event.exception.values[0].type = 'HttpErrorResponse';
    event.exception.values[0].value = value;
  }
  event.fingerprint = ['http-error', String(status), path];
  // Response bodies can carry guest and reservation data; status and path only.
  event.extra = { ...event.extra, status, url: path };
  return event;
}

/**
 * Query strings here carry reservation ids, guest emails and search terms.
 * Only the path is ever useful for diagnosis, so drop the rest.
 */
export function sanitizeUrl(url: string | null | undefined): string {
  if (!url) {
    return '';
  }
  const cut = url.search(/[?#]/);
  return cut === -1 ? url : url.slice(0, cut);
}

/** Bootstrap happens outside the injector, so this cannot go through SentryService. */
export function captureBootstrapError(error: unknown): void {
  if (environment.sentryEnabled) {
    Sentry.captureException(error, { tags: { phase: 'bootstrap' } });
  }
}

@Injectable({ providedIn: 'root' })
export class SentryService {
  /**
   * Identified by account id only — deliberately not email or name. The id is
   * enough to correlate a report with a user internally, without handing
   * account holders' personal details to a third party.
   */
  setUser(id: string | null): void {
    if (environment.sentryEnabled) {
      Sentry.setUser(id ? ({ id } satisfies User) : null);
    }
  }

  log(message: string): void {
    if (environment.sentryEnabled) {
      Sentry.captureMessage(message);
    }
  }

  logError(error: Error): void {
    if (environment.sentryEnabled) {
      Sentry.captureException(error);
    }
  }

  /**
   * Records a non-fatal event with structured context. Used for things that do
   * not throw but silently degrade the app (failed navigations, dropped
   * sockets, requests that never resolve).
   */
  logEvent(message: string, context: Record<string, unknown>): void {
    if (environment.sentryEnabled) {
      Sentry.captureMessage(message, { level: 'warning', extra: context });
    }
  }
}

/**
 * Replaces Angular's default ErrorHandler, which only writes to the console —
 * invisible on a device.
 */
@Injectable()
export class SentryErrorHandler implements ErrorHandler {
  handleError(error: unknown): void {
    // Keep console output so `cordova run --debug` and browser devtools are unaffected.
    console.error(error);

    if (!environment.sentryEnabled) {
      return;
    }

    // HttpErrorResponse is labelled centrally by beforeSend, so that errors
    // arriving via the SDK's own browser instrumentation — which bypass this
    // handler entirely — are formatted identically instead of landing as an
    // opaque "Object captured as exception with keys: ...".
    Sentry.captureException(unwrap(error));
  }
}

/** Angular wraps errors thrown in templates/DI; the cause carries the real one. */
function unwrap(error: unknown): unknown {
  return error && typeof error === 'object' && 'ngOriginalError' in error
    ? (error as { ngOriginalError: unknown }).ngOriginalError
    : error;
}
